import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookBookmark, CheckCircle, Warning, ArrowLeft } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";

/**
 * Five-question personal knowledge base. Sent to every GPT as a "USER
 * BACKGROUND" block at message-send time so the user never has to restate
 * these facts. Empty KB = original flow (GPTs ask each question themselves).
 */
const FIELDS = [
  {
    key: "niche",
    label: "What is your niche?",
    placeholder: "e.g. Fractional CFO for D2C founders doing ₹1–10 Cr.",
    rows: 2,
  },
  {
    key: "value_proposition",
    label: "What is your value proposition?",
    placeholder: "The single transformation you deliver — the before/after, in your client's words.",
    rows: 2,
  },
  {
    key: "target_industry",
    label: "What is your target industry?",
    placeholder: "e.g. D2C / ecommerce, B2B SaaS, hospitality, professional services.",
    rows: 1,
  },
  {
    key: "target_audience",
    label: "What is your target audience?",
    placeholder: "Role, size, stage. e.g. Founders + heads of growth at 1–10 Cr D2C brands.",
    rows: 2,
  },
  {
    key: "consulting_business_goal",
    label: "What kind of consulting business do you want to build?",
    placeholder: "Boutique / agency / advisory. Revenue or impact target + horizon.",
    rows: 2,
  },
];

const emptyKB = () => Object.fromEntries(FIELDS.map((f) => [f.key, ""]));

export default function KnowledgeBase() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [values, setValues] = useState(emptyKB());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/knowledge-base");
        const filled = emptyKB();
        FIELDS.forEach((f) => {
          filled[f.key] = data.knowledge_base?.[f.key] || "";
        });
        setValues(filled);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load your knowledge base");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filledCount = FIELDS.filter((f) => (values[f.key] || "").trim()).length;
  const isComplete = filledCount === FIELDS.length;
  const isEmpty = filledCount === 0;

  const onChange = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    setSavedAt(null);
  };

  const onSave = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put("/knowledge-base", values);
      const next = emptyKB();
      FIELDS.forEach((f) => {
        next[f.key] = data.knowledge_base?.[f.key] || "";
      });
      setValues(next);
      setSavedAt(new Date());
      // Update the cached user object so the navbar badge clears immediately
      if (user && typeof user === "object") {
        setUser({ ...user, knowledge_base_filled: data.is_filled });
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="knowledge-base-page">
      <Navbar />
      <main className="kb-shell">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="kb-back"
          data-testid="kb-back"
        >
          <ArrowLeft size={14} weight="bold" /> Back to Dashboard
        </button>

        <header className="kb-head">
          <div className="kb-eyebrow-wrap">
            <span className="kb-eyebrow-icon">
              <BookBookmark size={16} weight="duotone" />
            </span>
            <p className="label-uppercase kb-eyebrow">Knowledge Base</p>
          </div>
          <h1 className="kb-title">
            Tell every GPT who you are, <em>once</em>.
          </h1>
          <p className="kb-sub">
            Answer these five questions and every GPT in the hub will treat
            them as known facts — no more restating your niche, audience and
            goals at the start of each conversation.
          </p>
          <div className="kb-status" data-testid="kb-status">
            {isComplete ? (
              <span className="kb-status-pill kb-status-complete">
                <CheckCircle size={14} weight="fill" /> Complete · all 5 answered
              </span>
            ) : isEmpty ? (
              <span className="kb-status-pill kb-status-empty">
                <Warning size={14} weight="fill" /> Empty · GPTs will run the normal flow
              </span>
            ) : (
              <span className="kb-status-pill kb-status-partial">
                <Warning size={14} weight="fill" /> {filledCount} of {FIELDS.length} answered
              </span>
            )}
          </div>
        </header>

        {loading ? (
          <div className="py-16 flex justify-center" data-testid="kb-loading">
            <div className="spinner" />
          </div>
        ) : (
          <form onSubmit={onSave} className="kb-form" data-testid="kb-form">
            {FIELDS.map((f, idx) => (
              <div key={f.key} className="kb-field" data-testid={`kb-field-${f.key}`}>
                <label className="kb-label" htmlFor={`kb-${f.key}`}>
                  <span className="kb-num">{String(idx + 1).padStart(2, "0")}</span>
                  <span>{f.label}</span>
                </label>
                <textarea
                  id={`kb-${f.key}`}
                  className="textarea kb-textarea"
                  rows={f.rows}
                  value={values[f.key] || ""}
                  onChange={(e) => onChange(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  data-testid={`kb-input-${f.key}`}
                />
              </div>
            ))}

            {error && (
              <p className="error-banner" data-testid="kb-error">
                {error}
              </p>
            )}

            <div className="kb-actions">
              {savedAt && (
                <span className="kb-saved" data-testid="kb-saved">
                  <CheckCircle size={14} weight="fill" />
                  Saved at {savedAt.toLocaleTimeString()}
                </span>
              )}
              <button
                type="submit"
                className="btn btn-accent"
                disabled={saving}
                data-testid="kb-save"
              >
                {saving ? "Saving…" : "Save knowledge base"}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
